import { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  Sparkles,
  TrendingUp,
  Target,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Lightbulb,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import aiAdvisorService from '../../services/aiAdvisorService'

const COLLAPSE_STORAGE_KEY = 'progress:aiAdvisor:collapsed'

/**
 * AI Advisor card for the student dashboard.
 *
 * Renders a grounded, structured advisor report (summary / achievements /
 * struggles with likely reasons / recommendations). Generation is on-demand:
 * the student clicks "Generate insights". When their results change since the
 * last report, an explicit "Update report" affordance appears - there is no
 * silent regeneration. Hidden entirely when the add-on is disabled or the
 * student has opted out.
 */
export default function AIAdvisorCard({ defaultCollapsed = false }) {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(null)
  // Only Progress opts into collapsibility (defaultCollapsed). The Dashboard keeps the card
  // expanded. When collapsible, remember the student's open/closed choice across navigation.
  const [collapsed, setCollapsed] = useState(() => {
    if (!defaultCollapsed) return false
    try {
      const saved = localStorage.getItem(COLLAPSE_STORAGE_KEY)
      if (saved === 'false') return false
      if (saved === 'true') return true
    } catch {
      /* localStorage unavailable - fall back to collapsed */
    }
    return true
  })

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, String(next))
      } catch {
        /* ignore persistence failures */
      }
      return next
    })
  }, [])

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true)
      const data = await aiAdvisorService.getStatus()
      setStatus(data)
    } catch {
      setError('Could not load your advisor.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  const handleGenerate = useCallback(async (force) => {
    setGenerating(true)
    setError(null)
    try {
      const report = await aiAdvisorService.generate(force)
      setStatus((prev) => ({
        ...prev,
        hasReport: true,
        report,
        isStale: false,
        newChallengesSince: 0,
        usedToday: (prev?.usedToday || 0) + (report.cached ? 0 : 1)
      }))
    } catch (err) {
      const code = err?.response?.data?.error?.code
      if (code === 'DAILY_CAP') setError("You've reached today's update limit. Try again tomorrow.")
      else if (code === 'INSUFFICIENT_QUESTIONS')
        setError(
          err?.response?.data?.error?.message ||
            'Answer a few more questions to unlock your next report.'
        )
      else setError('Sorry, we could not generate your insights right now. Please try again.')
    } finally {
      setGenerating(false)
    }
  }, [])

  // Hidden when the feature is off or the student opted out. Render nothing while status is loading
  // too: the card is mounted on every dashboard, but the feature is off by default, so a visible
  // "loading" box would flash for students who will never see the card.
  if (loading) return null
  if (!status || !status.featureEnabled || !status.optedIn) return null

  const report = status.report?.content
  const collapsible = defaultCollapsed
  const showBody = !collapsible || !collapsed
  // One-line hint shown when collapsed, so the student knows there is more to open.
  const teaser =
    report?.summary ||
    'Get a personalized read on your progress: what is working, where you are stuck, and what to focus on next.'

  return (
    <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-white to-indigo-50/40 p-6 shadow-sm dark:border-indigo-900/40 dark:from-gray-800 dark:to-indigo-950/20">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-indigo-100 p-2 dark:bg-indigo-900/40">
            <Sparkles className="h-5 w-5 text-indigo-600 dark:text-indigo-300" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Your AI Advisor
            </h2>
            {status.report?.createdAt && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Generated{' '}
                {new Date(status.report.createdAt).toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short'
                })}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showBody && status.hasReport && (
            <button
              type="button"
              onClick={() => handleGenerate(true)}
              disabled={generating || status.capReached || !status.canGenerate}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              title={
                status.capReached
                  ? "You've reached today's update limit"
                  : !status.canGenerate
                    ? `Answer ${Math.max(0, status.questionsRequired - status.questionsTowardNext)} more question(s) to update`
                    : 'Regenerate'
              }
            >
              <RefreshCw className={`h-4 w-4 ${generating ? 'animate-spin' : ''}`} />
              Update
            </button>
          )}
          {collapsible && (
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-expanded={!collapsed}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              {collapsed ? 'Show insights' : 'Hide'}
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Collapsed teaser - a one-line hint that there is more to open */}
      {collapsible && collapsed && (
        <button type="button" onClick={toggleCollapsed} className="w-full text-left">
          <p className="line-clamp-1 text-sm text-gray-600 dark:text-gray-300">{teaser}</p>
        </button>
      )}

      {/* "Results changed" affordance — only once the question gate allows an update */}
      {status.hasReport && status.isStale && status.canGenerate && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/40 dark:bg-amber-950/20">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Your results have changed
            {status.newChallengesSince > 0
              ? ` (${status.newChallengesSince} new challenge${status.newChallengesSince === 1 ? '' : 's'})`
              : ''}
            . Update your report for the latest insights.
          </p>
          <button
            type="button"
            onClick={() => handleGenerate(true)}
            disabled={generating || status.capReached}
            className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            Update report
          </button>
        </div>
      )}

      {/* Question-count gate: how many more answers until the next update unlocks */}
      {status.hasReport && !status.canGenerate && (
        <UnlockProgress
          current={status.questionsTowardNext}
          required={status.questionsRequired}
          hasReport
        />
      )}

      {error && <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {/* Empty state */}
      {showBody && !status.hasReport && (
        <div className="py-6 text-center">
          <p className="mx-auto mb-4 max-w-md text-sm text-gray-600 dark:text-gray-300">
            Get a personalized read on your progress: what you are doing well, where you are stuck
            and why, and exactly what to focus on next.
          </p>
          {status.canGenerate ? (
            <button
              type="button"
              onClick={() => handleGenerate(false)}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Generate insights
            </button>
          ) : (
            <UnlockProgress
              current={status.questionsTowardNext}
              required={status.questionsRequired}
              hasReport={false}
            />
          )}
        </div>
      )}

      {/* Report */}
      {showBody && report && (
        <div className="space-y-5">
          {report.summary && (
            <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-200">
              {report.summary}
            </p>
          )}

          {report.achievements?.length > 0 && (
            <Section
              icon={<TrendingUp className="h-4 w-4 text-emerald-600" />}
              title="What's working"
            >
              {report.achievements.map((a, i) => (
                <li key={`${a.title}-${i}`} className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{a.title}.</span>{' '}
                  {a.detail}
                </li>
              ))}
            </Section>
          )}

          {report.strugglingAreas?.length > 0 && (
            <Section
              icon={<AlertTriangle className="h-4 w-4 text-amber-600" />}
              title="Where you're stuck"
            >
              {report.strugglingAreas.map((s, i) => (
                <li key={`${s.domain}-${i}`} className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{s.domain}:</span>{' '}
                  {s.observation}{' '}
                  <span className="text-gray-500 dark:text-gray-400">
                    Likely reason: {s.likelyReason}
                  </span>
                </li>
              ))}
            </Section>
          )}

          {report.recommendations?.length > 0 && (
            <Section icon={<Target className="h-4 w-4 text-indigo-600" />} title="Your next steps">
              {report.recommendations.map((r, i) => (
                <li key={`${r.action}-${i}`} className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{r.action}.</span>{' '}
                  {r.why}
                </li>
              ))}
            </Section>
          )}

          {report.coachNote && (
            <div className="flex items-start gap-2 rounded-lg bg-indigo-50 px-4 py-3 dark:bg-indigo-950/30">
              <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-300" />
              <p className="text-sm italic text-gray-700 dark:text-gray-300">{report.coachNote}</p>
            </div>
          )}

          <p className="text-right text-[11px] text-gray-400">
            AI-generated from your practice data
            {status.report?.createdAt
              ? ` on ${new Date(status.report.createdAt).toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short'
                })}`
              : ''}
            . Always use your judgment.
          </p>
        </div>
      )}
    </div>
  )
}

AIAdvisorCard.propTypes = {
  // When true, the card is collapsible and starts collapsed (Progress page). Default false keeps
  // the Dashboard's always-expanded behavior.
  defaultCollapsed: PropTypes.bool
}

function Section({ icon, title, children }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5">
        {icon}
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      <ul className="ml-1 list-inside list-disc space-y-1.5">{children}</ul>
    </div>
  )
}

/**
 * Progress toward the next advisor unlock. Both numbers are the student's LIFETIME answered-question
 * count: the advisor unlocks each time that count crosses a multiple of 100, so a student who
 * generated a report at 180 answered questions sees "180 / 200" until they answer 20 more.
 */
function UnlockProgress({ current, required, hasReport }) {
  const safeRequired = required > 0 ? required : 1
  const pct = Math.min(100, Math.round((Math.max(0, current) / safeRequired) * 100))
  const remaining = Math.max(0, required - current)
  return (
    <div className="mx-auto mb-2 max-w-md rounded-lg border border-indigo-100 bg-indigo-50/50 px-4 py-3 text-left dark:border-indigo-900/40 dark:bg-indigo-950/20">
      <div className="mb-1 flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
        <span>{hasReport ? 'Questions since last report' : 'Questions answered'}</span>
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          {current} / {required}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className="h-2 rounded-full bg-indigo-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        {hasReport
          ? `Your next update unlocks after ${remaining} more question${remaining === 1 ? '' : 's'}.`
          : `Your first report unlocks after ${remaining} more question${remaining === 1 ? '' : 's'}.`}
      </p>
    </div>
  )
}
